import React, {
    useCallback, useEffect, useMemo, useRef, useState,
} from 'react';

import Card from 'components/Card/Card';
import Preloader from 'components/Preloader';
import EmptyContent from 'components/EmptyContent';

import {
    Modal, Tabs, TabPane, Table, Pagination,
    Icon,
} from 'ui-library';
import { LangSource, languagesAdapt } from 'translations/adaptors/languagesAdapt';
import { useTranslation } from 'react-i18next';

import { withMainLayout } from 'hocs';
import { useGetCustomService } from 'hooks/rest/useGetCustomService';
import { useGenericAPIMutation } from 'hooks/mutations/useGenericAPIMutation';

import { ICON_TYPE_CLOSE } from 'ui-library/components/Icon';
import {
    adaptPreviewMessageData,
    adaptSystemMessageData,
    adaptSystemMessageDetailsData,
    fetchDocumentData,
    getSystemMessageColumns,
    SystemMessageStatus,
    getStatus,
} from './utils';
import { Language, IFormType } from './SystemMessage.types';
import SystemMessageFormModal from './SystemMessageFormModal';
import PreviewSystemMessage from './PreviewSystemMessage';

import './SystemMessage.css';

const SystemMessages = () => {
    const {
        t,
        i18n: { language },
    } = useTranslation();

    const [showModal, setShowModal] = useState<IFormType>(undefined);
    const [currentTab, setCurrentTab] = useState<Language>('de');
    const [messagePreviewData, setMessagePreviewData] = useState<any>(null);
    const [editMessageData, setEditMessageData] = useState<any>(null);
    const [messageDocuments, setMessageDocuments] = useState<any[] | null>(
        null,
    );
    const [loadingEditMessage, setLoadingEditMessage] = useState<string>('');

    const [pagination, setPagination] = useState({
        page: 1,
        pageSize: 10,
    });

    const [activeMessageId, setActiveMessageId] = useState<number | null>(null);
    const [activeMessageStatus, setActiveMessageStatus] = useState<SystemMessageStatus | null>(null);

    const handleOpenMessageAbort = useRef(false);

    const languages: LangSource[] = useMemo(
        () => languagesAdapt(globalThis.availableLanguages, t),
        [language],
    );

    const {
        data: systemMessagesData,
        isLoading: systemMessagesLoading,
        error: systemMessagesLoadingError,
    } = useGetCustomService({
        service: 'customSystemMessageService',
        api: 'getSystemMessages',
        payload: {
            page: pagination.page,
            pageSize: pagination.pageSize,
            language,
        },
        enabled: true,
        gcTime: 1000 * 60 * 1,
        staleTime: 1000 * 60 * 1,
    });

    const {
        data: systemMessageDetailsData,
        isLoading: systemMessageDetailsLoading,
    } = useGetCustomService({
        service: 'customSystemMessageService',
        api: 'getSystemMessageDetails',
        payload: { messageId: activeMessageId },
        enabled: !!activeMessageId && showModal !== 'delete-message',
    });

    const { mutateAsync: handleMutation } = useGenericAPIMutation();

    useEffect(() => {
        if (systemMessageDetailsData?.data) {
            if (showModal === 'preview-message') {
                const messageDetails = adaptPreviewMessageData({
                    data: systemMessageDetailsData?.data,
                    documents: messageDocuments,
                    t,
                });

                setMessagePreviewData(messageDetails);
            } else {
                const messageDetails = adaptSystemMessageDetailsData({
                    data: systemMessageDetailsData?.data,
                    documents: messageDocuments,
                    duplicate: showModal === 'duplicate-message',
                    languages: languages.map((l) => l.value),
                });

                setEditMessageData(messageDetails);
            }
        } else {
            setMessagePreviewData(null);
            setEditMessageData(null);
        }
    }, [systemMessageDetailsData?.data, messageDocuments, showModal, t]);

    const handleCreateMessage = useCallback(() => {
        setShowModal('create-message');
        setEditMessageData(null);
        setActiveMessageId(null);
    }, []);

    const handleOpenMessage = useCallback(async ({ data, type }) => {
        if (type === 'delete-message') {
            const messageStatus = getStatus(
                data?.startDateTime,
                data?.endDateTime,
            );

            setActiveMessageId(data?.id);
            setActiveMessageStatus(messageStatus);
            setShowModal(type);

            return;
        }

        setCurrentTab('de');
        if (data?.uId) {
            handleOpenMessageAbort.current = false;
            try {
                setLoadingEditMessage(type);
                const response: any = await handleMutation({
                    service: 'documents',
                    API: 'postDocumentsList',
                    payload: {
                        page: 1,
                        pageSize: 10,
                        contractId: data?.uId,
                        isActive: true,
                    },
                });

                const documents = await fetchDocumentData({
                    documents:
                        response?.data?.results?.filter(
                            (doc) => doc?.isActive,
                        ) || [],
                });

                if (!handleOpenMessageAbort.current) {
                    setMessageDocuments(documents);
                    setActiveMessageId(data?.id);
                    setShowModal(type);
                    setLoadingEditMessage('');
                }
            } catch (error) {
                if (!handleOpenMessageAbort.current) setLoadingEditMessage('');
            }
        }
    }, [handleMutation]);

    const systemMessages = useMemo(
        () => adaptSystemMessageData({
            data: systemMessagesData?.data?.results,
            handleOpenMessage,
            loadingEditMessage,
            t,
        }) || [],
        [
            systemMessagesData?.data?.results,
            language,
            loadingEditMessage,
            handleOpenMessage,
        ],
    );

    const systemMessageColumns = useMemo(
        () => getSystemMessageColumns({ t }),
        [t],
    );

    useEffect(() => () => { handleOpenMessageAbort.current = true; }, []);

    const onCloseModal = () => {
        handleOpenMessageAbort.current = true;
        setShowModal(undefined);
        setEditMessageData(null);
        setActiveMessageId(null);
    };

    const onCreateOrEditSystemMessageSuccess = () => {
        onCloseModal();
    };

    const [isLoadingDeleteFN, setIsLoadingDeleteFN] = useState(false);

    const onSystemMessageDelete = useCallback(async () => {
        setIsLoadingDeleteFN(true);
        await handleMutation({
            service: 'customSystemMessageService',
            API: activeMessageStatus === 'active' ? 'deactivateSystemMessage' : 'deleteSystemMessage',
            payload: { messageId: activeMessageId },
            invalidateQueryKeys: ['getData_getSystemMessages', 'getData_getActiveSystemMessageDetails'],
        });
        onCloseModal();
        setIsLoadingDeleteFN(false);
    }, [activeMessageId, activeMessageStatus, handleMutation]);

    return (
        <>
            <div className="container system-messages">
                <Card
                    title={t('navigation.sideNav.systemMessages')}
                    headerActions={(
                        <button
                            type="button"
                            className="button-link"
                            onClick={handleCreateMessage}
                        >
                            {t('SystemMessage.Button.CreateNewMessage')}
                        </button>
                    )}
                >
                    <Preloader
                        isLoading={systemMessagesLoading}
                        error={systemMessagesLoadingError}
                    >
                        <EmptyContent
                            data={systemMessages}
                            text={t('advisoryDashboard.noData')}
                        >
                            <Table
                                className="Table--ext system-messages-table"
                                columns={systemMessageColumns}
                                data={systemMessages}
                            />

                            <Pagination
                                pageSize={pagination.pageSize}
                                total={
                                    systemMessagesData?.data?.totalCount ?? 0
                                }
                                currentPage={pagination.page}
                                onChange={(page) => setPagination((prev) => ({
                                    ...prev,
                                    page: page as number,
                                }))}
                            />
                        </EmptyContent>
                    </Preloader>
                </Card>
            </div>

            {/* Create message modal */}
            <SystemMessageFormModal
                visible={showModal === 'create-message'}
                languages={languages}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                onCloseModal={onCloseModal}
                messageData={null}
                onSuccess={onCreateOrEditSystemMessageSuccess}
                formType={showModal}
                t={t}
            />

            {/* Edit / Duplicate message modal */}
            <SystemMessageFormModal
                visible={
                    ['edit-message', 'duplicate-message'].includes(showModal as string)
                    && !!activeMessageId
                }
                languages={languages}
                currentTab={currentTab}
                setCurrentTab={setCurrentTab}
                onCloseModal={onCloseModal}
                messageData={editMessageData}
                onSuccess={onCreateOrEditSystemMessageSuccess}
                formType={showModal}
                isLoading={systemMessageDetailsLoading || !editMessageData}
                t={t}
            />

            {/* Preview message modal */}
            <Modal
                visible={
                    showModal === 'preview-message' && !!messagePreviewData
                }
                width={800}
                height="90%"
                header={<span />}
                footer={<span />}
                borderRadius
                className="system-message-modal"
            >
                <Tabs
                    activeKey={currentTab}
                    onChange={(key) => setCurrentTab(key)}
                    className="system-message-language-tab"
                >
                    {languages?.map((lang) => (
                        <TabPane
                            tab={(
                                <div className="tab-title">
                                    <span>{lang?.label || ''}</span>
                                </div>
                            )}
                            key={lang?.value}
                        />
                    ))}
                </Tabs>
                <PreviewSystemMessage
                    title={
                        messagePreviewData?.translations?.title?.[currentTab]
                    }
                    body={messagePreviewData?.translations?.body?.[currentTab]}
                    link={messagePreviewData?.translations?.link?.[currentTab]}
                    document={
                        messagePreviewData?.translations?.document?.[currentTab]
                            ?.file
                    }
                    startDate={messagePreviewData?.startDate}
                    endDate={messagePreviewData?.endDate}
                    platforms={messagePreviewData?.platforms}
                    t={t}
                    onClose={onCloseModal}
                />
            </Modal>

            {/* Delete message modal */}
            <Modal
                title={t(activeMessageStatus === 'active' ? 'SystemMessage.Modal.Deactivate.Title' : 'SystemMessage.Modal.Delete.Title')}
                visible={showModal === 'delete-message'}
                width={560}
                cancelText={t('clientDashboard.cancel')}
                okText={t(activeMessageStatus === 'active' ? 'SystemMessage.Table.ActionBtn.DeactivateMessage' : 'clientDashboard.delete')}
                onCancel={onCloseModal}
                onOk={onSystemMessageDelete}
                okButtonProps={{ loading: isLoadingDeleteFN }}
                borderRadius
                className="system-message-delete-modal"
                closable
                closeIcon={
                    <Icon type={ICON_TYPE_CLOSE} size={16} />
                }
            >
                <p>{t(activeMessageStatus === 'active' ? 'SystemMessage.Modal.Deactivate.Description' : 'SystemMessage.Modal.Delete.Description')}</p>
            </Modal>
        </>
    );
};

export default withMainLayout(SystemMessages);
